import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, XCircle, Users } from "lucide-react";
import { supabase } from "../../lib/supabase";
import { accepterInvitation, getRoleLabel } from "../../lib/equipe";
import { useDossierStore } from "../../store/dossierStore";

const schema = z
  .object({
    nom: z.string().min(2, "Nom requis"),
    prenom: z.string().min(2, "Prénom requis"),
    password: z.string().min(8, "8 caractères minimum"),
    confirmPassword: z.string(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: "Les mots de passe ne correspondent pas",
    path: ["confirmPassword"],
  });

type FormData = z.infer<typeof schema>;

type InvitationInfo = {
  id: string;
  dossier_id: string;
  email: string;
  role: "daf" | "directeur_financier" | "comptable";
  dossier_nom: string;
  expires_at: string;
  acces_gestion: boolean;
  acces_compta: boolean;
};

export default function InvitationCollaborateur() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [status, setStatus] = useState<"loading" | "valid" | "expired" | "error">("loading");
  const [invitation, setInvitation] = useState<InvitationInfo | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authState, setAuthState] = useState<"checking" | "none" | "match" | "mismatch">("checking");
  const [currentEmail, setCurrentEmail] = useState<string>("");
  const [autoAcceptDone, setAutoAcceptDone] = useState(false);
  const { setActiveDossier } = useDossierStore();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      prenom: "",
      nom: "",
      password: "",
      confirmPassword: "",
    },
  });

  useEffect(() => {
    const checkToken = async () => {
      const tokenFromUrl = new URLSearchParams(window.location.search).get("token");
      console.log("[INVITE] token from URL:", tokenFromUrl);
      const effectiveToken = tokenFromUrl ?? token;

      if (!effectiveToken) {
        setStatus("error");
        return;
      }

      const { data, error } = await supabase
        .from("dossier_invitations")
        .select("id, dossier_id, email, role, expires_at, acces_gestion, acces_compta")
        .eq("token", effectiveToken)
        .eq("status", "pending")
        .gt("expires_at", new Date().toISOString())
        .single();

      console.log("[INVITE] query result:", data, error);

      if (error || !data) {
        setStatus("error");
        return;
      }

      let dossierNom = "Dossier";
      const { data: authData } = await supabase.auth.getUser();
      if (authData.user) {
        const { data: dossierData, error: dossierErr } = await supabase
          .from("dossiers")
          .select("nom")
          .eq("id", data.dossier_id)
          .single();
        if (dossierErr) {
          console.log("[INVITE] dossier name query result:", dossierData, dossierErr);
        } else {
          dossierNom = String(dossierData?.nom ?? "Dossier");
        }
      }

      setInvitation({
        id: data.id,
        dossier_id: data.dossier_id,
        email: data.email,
        role: data.role,
        dossier_nom: dossierNom,
        expires_at: data.expires_at,
        acces_gestion: Boolean(data.acces_gestion),
        acces_compta: Boolean(data.acces_compta),
      });
      reset({
        prenom: "",
        nom: "",
        password: "",
        confirmPassword: "",
      });
      setStatus("valid");
    };

    void checkToken();
  }, [token, reset]);

  useEffect(() => {
    if (status !== "valid" || !invitation) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (cancelled) return;
      const user = data.user;
      if (!user) {
        setCurrentEmail("");
        setAuthState("none");
        return;
      }
      const current = (user.email ?? "").toLowerCase().trim();
      const invited = invitation.email.toLowerCase().trim();
      setCurrentEmail(current);
      setAuthState(current === invited ? "match" : "mismatch");
    })();
    return () => {
      cancelled = true;
    };
  }, [status, invitation]);

  const finalizeAcceptedInvitation = async (inv: InvitationInfo, inviteToken: string) => {
    console.log("[INVITE] invitation.dossier_id:", inv.dossier_id);
    const { data: authData } = await supabase.auth.getUser();
    const payloadDossierMembres = {
      dossier_id: inv.dossier_id,
      user_id: authData.user?.id ?? null,
      role: inv.role,
      acces_gestion: inv.acces_gestion,
      acces_compta: inv.acces_compta,
    };
    console.log("[INVITE] insert dossier_membres payload:", payloadDossierMembres);
    await accepterInvitation(inviteToken);
    const { data: memberRow, error: memberErr } = await supabase
      .from("dossier_membres")
      .select("id, dossier_id, user_id, role, acces_gestion, acces_compta")
      .eq("dossier_id", inv.dossier_id)
      .eq("user_id", authData.user?.id ?? "")
      .maybeSingle();
    console.log("[INVITE] dossier_membres after accept:", memberRow, memberErr);
    setActiveDossier({ id: inv.dossier_id, nom: inv.dossier_nom });
    console.log("[INVITE] redirect to dossier:", inv.dossier_id);
    navigate("/dashboard", { replace: true });
  };

  useEffect(() => {
    if (!invitation || !token) return;
    if (status !== "valid" || authState !== "match" || autoAcceptDone) return;
    setAutoAcceptDone(true);
    setCreating(true);
    setError(null);
    void (async () => {
      try {
        await finalizeAcceptedInvitation(invitation, token);
      } catch (e) {
        setAutoAcceptDone(false);
        setCreating(false);
        setError(e instanceof Error ? e.message : "Erreur lors de l'acceptation de l'invitation");
      }
    })();
  }, [status, authState, invitation, token, autoAcceptDone]);

  const onSubmit = async (data: FormData) => {
    if (!invitation || !token || authState !== "none") return;

    setCreating(true);
    setError(null);

    try {
      const { error: signUpError } = await supabase.auth.signUp({
        email: invitation.email,
        password: data.password,
        options: {
          data: {
            nom: data.nom,
            prenom: data.prenom,
          },
        },
      });
      if (signUpError) throw signUpError;

      // Assure une session active pour auth.uid() côté RPC.
      const { data: signInData, error: signInErr } = await supabase.auth.signInWithPassword({
        email: invitation.email,
        password: data.password,
      });
      if (signInErr) throw signInErr;
      if (!signInData.user) throw new Error("Impossible de se connecter après création du compte.");
      console.log("[INVITE] user.id after signup:", signInData.user.id);

      await finalizeAcceptedInvitation(invitation, token);
    } catch (e) {
      console.error("[INVITE_ACCEPT] erreur acceptation", {
        error: e,
        message: e instanceof Error ? e.message : String(e),
        token,
        invitation_id: invitation?.id,
        dossier_id: invitation?.dossier_id,
      });
      setError(e instanceof Error ? e.message : "Erreur lors de la création du compte");
      setCreating(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    background: "#0A0A08",
    border: "1px solid #2A2520",
    borderRadius: "10px",
    padding: "14px 16px",
    color: "#F5F0E8",
    fontSize: "0.95rem",
    fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
    outline: "none",
    transition: "border-color 0.2s",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "0.8rem",
    color: "#9A8F7E",
    marginBottom: "8px",
    fontWeight: 600,
  };

  if (status === "loading") {
    return (
      <div
        style={{
          fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
          background: "#0A0A0A",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loader2 size={40} style={{ color: "#F97316", animation: "spin 1s linear infinite" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (status === "error" || status === "expired") {
    return (
      <div
        style={{
          fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
          background: "#0A0A0A",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
        }}
      >
        <div
          style={{
            background: "#111110",
            borderRadius: "20px",
            border: "1px solid #2A2520",
            padding: "48px 40px",
            textAlign: "center",
            maxWidth: "420px",
          }}
        >
          <XCircle size={56} style={{ color: "#EF4444", marginBottom: "20px" }} />
          <h1 style={{ fontSize: "1.4rem", fontWeight: 700, color: "#F5F0E8", margin: "0 0 12px" }}>
            {status === "expired" ? "Invitation expirée" : "Invitation invalide"}
          </h1>
          <p style={{ fontSize: "0.95rem", color: "#6A6050", margin: "0 0 28px", lineHeight: 1.6 }}>
            {status === "expired"
              ? "Cette invitation a expiré. Demandez à l'administrateur de vous renvoyer une invitation."
              : "Ce lien d'invitation n'est pas valide ou a déjà été utilisé."}
          </p>
          <Link
            to="/equipe/login"
            style={{
              display: "inline-block",
              padding: "12px 24px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #F97316, #EAB308)",
              color: "#0A0A0A",
              fontSize: "0.9rem",
              fontWeight: 600,
              textDecoration: "none",
            }}
          >
            Aller à la connexion
          </Link>
        </div>
      </div>
    );
  }

  if (!invitation) return null;

  return (
    <div
      style={{
        fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
        background: "linear-gradient(135deg, #0A0A0A 0%, #1a1a18 100%)",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <style>{`
        input:focus { border-color: #F97316 !important; }
        input::placeholder { color: #3A3530; }
      `}</style>

      <div
        style={{
          width: "100%",
          maxWidth: "460px",
          background: "#111110",
          borderRadius: "20px",
          border: "1px solid #2A2520",
          padding: "40px 36px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "28px" }}>
          <div
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "14px",
              background: "linear-gradient(135deg, #F97316, #EAB308)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            <Users size={28} color="#0A0A0A" />
          </div>
          <h1 style={{ fontSize: "1.4rem", fontWeight: 700, color: "#F5F0E8", margin: "0 0 8px" }}>
            Rejoindre l'équipe
          </h1>
          <p style={{ fontSize: "0.9rem", color: "#6A6050", margin: 0 }}>
            {authState === "match"
              ? "Votre email correspond à l'invitation. Validation en cours..."
              : "Créez votre compte pour accéder au dossier"}
          </p>
        </div>

        <div
          style={{
            background: "rgba(249, 115, 22, 0.08)",
            border: "1px solid rgba(249, 115, 22, 0.2)",
            borderRadius: "12px",
            padding: "16px 18px",
            marginBottom: "28px",
          }}
        >
          <p style={{ fontSize: "0.85rem", color: "#F97316", margin: "0 0 8px", fontWeight: 600 }}>
            Vous êtes invité(e) en tant que {getRoleLabel(invitation.role)}
          </p>
          <p style={{ fontSize: "0.95rem", color: "#F5F0E8", margin: "0 0 4px" }}>📁 {invitation.dossier_nom}</p>
          <p style={{ fontSize: "0.8rem", color: "#6A6050", margin: 0 }}>{invitation.email}</p>
        </div>

        {authState === "mismatch" ? (
          <div
            style={{
              background: "rgba(239, 68, 68, 0.1)",
              border: "1px solid rgba(239, 68, 68, 0.35)",
              borderRadius: "10px",
              padding: "14px 16px",
              marginBottom: "20px",
            }}
          >
            <p style={{ fontSize: "0.88rem", color: "#fecaca", margin: "0 0 12px", lineHeight: 1.5 }}>
              Vous êtes connecté avec <strong>{currentEmail || "un autre compte"}</strong>. Cette invitation est pour{" "}
              <strong>{invitation.email}</strong>. Déconnectez-vous d'abord ou utilisez un autre navigateur.
            </p>
            <button
              type="button"
              onClick={() => void supabase.auth.signOut().then(() => window.location.reload())}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "10px",
                border: "1px solid rgba(248, 113, 113, 0.55)",
                background: "rgba(127, 29, 29, 0.35)",
                color: "#fecaca",
                fontSize: "0.9rem",
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
              }}
            >
              Se déconnecter
            </button>
          </div>
        ) : authState === "match" ? (
          <div style={{ textAlign: "center", color: "#6A6050", marginBottom: 20 }}>
            <Loader2 size={22} style={{ color: "#F97316", animation: "spin 1s linear infinite", margin: "0 auto 10px" }} />
            Validation de l'invitation...
          </div>
        ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div style={{ marginBottom: "20px" }}>
            <label style={labelStyle}>Email invité</label>
            <input value={invitation.email} readOnly style={{ ...inputStyle, opacity: 0.85, cursor: "not-allowed" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "20px" }}>
            <div>
              <label style={labelStyle}>Prénom</label>
              <input {...register("prenom")} placeholder="Amadou" style={inputStyle} />
              {errors.prenom && (
                <p style={{ fontSize: "0.75rem", color: "#EF4444", marginTop: "4px" }}>{errors.prenom.message}</p>
              )}
            </div>
            <div>
              <label style={labelStyle}>Nom</label>
              <input {...register("nom")} placeholder="Koné" style={inputStyle} />
              {errors.nom && (
                <p style={{ fontSize: "0.75rem", color: "#EF4444", marginTop: "4px" }}>{errors.nom.message}</p>
              )}
            </div>
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={labelStyle}>Mot de passe</label>
            <input {...register("password")} type="password" placeholder="••••••••" style={inputStyle} />
            {errors.password && (
              <p style={{ fontSize: "0.75rem", color: "#EF4444", marginTop: "4px" }}>{errors.password.message}</p>
            )}
          </div>

          <div style={{ marginBottom: "28px" }}>
            <label style={labelStyle}>Confirmer le mot de passe</label>
            <input {...register("confirmPassword")} type="password" placeholder="••••••••" style={inputStyle} />
            {errors.confirmPassword && (
              <p style={{ fontSize: "0.75rem", color: "#EF4444", marginTop: "4px" }}>
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          {error && (
            <div
              style={{
                background: "rgba(239, 68, 68, 0.1)",
                border: "1px solid rgba(239, 68, 68, 0.3)",
                borderRadius: "10px",
                padding: "12px 16px",
                marginBottom: "20px",
              }}
            >
              <p style={{ fontSize: "0.85rem", color: "#EF4444", margin: 0 }}>{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={creating}
            style={{
              width: "100%",
              padding: "14px",
              borderRadius: "10px",
              border: "none",
              background: creating ? "#3A3530" : "linear-gradient(135deg, #F97316, #EAB308)",
              color: creating ? "#6A6050" : "#0A0A0A",
              fontSize: "0.95rem",
              fontWeight: 700,
              cursor: creating ? "not-allowed" : "pointer",
              fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
              transition: "all 0.3s",
            }}
          >
            {creating ? "Création en cours..." : "Créer mon compte et rejoindre →"}
          </button>
        </form>
        )}

        <p style={{ textAlign: "center", fontSize: "0.85rem", color: "#4A4540", marginTop: "24px" }}>
          Déjà un compte ?{" "}
          <Link to="/equipe/login" style={{ color: "#F97316", textDecoration: "none", fontWeight: 600 }}>
            Se connecter
          </Link>
        </p>
      </div>
    </div>
  );
}
